import { AuthContext } from '../../context/AuthContext';
import {
    createManualOperationId,
    ManualReservation,
    ManualOperation,
    newManualOperation,
    selectedManualItems,
    pendingManualOperations,
    savePendingManualOperation,
    removePendingManualOperation,
} from '../../utils/manual-handout-operation';
import { AddHandoutProps, Filter, User } from '../../models/table-models';
import { ItemQuantity, ManualHandout } from '../../models/warehouse-models';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import {
    checkForItemsInInventory,
    getQuantityCount,
    manualHandoutBatch,
    reserveManualHandout,
    cancelManualHandout,
    useGetAllCompletedHandouts,
    useGetAllQuantities,
    useGetAllShortOtherMaterialPassports,
    useGetPassportTypesForWarehouse,
    useGetReferences,
    useGetReferencesForDistributor,
    useGetUserInventory,
    useGetWarehouses,
} from '../../utils/warehouses-hooks';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import Select, { SingleValue } from 'react-select';
import { distributorSelectStyles } from '../../styles/selectStyles';
import React, { Suspense, useContext, useEffect, useRef, useState } from 'react';
import { useGetUsers } from '../../utils/users-hooks';
import { NoDataComponent } from '../../components/NoDataComponent';
import { TableRequestParams } from '../../utils/data-hooks';
import { fetchMapBranches, fetchTasksAll, fetchTasksYours, updateTaskNeededItems } from '../../utils/dashboard-hooks';
import { useGetAllTaskSkeletons } from '../../utils/work-hooks';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../../components/CustomToastContent';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import { shortHandoutQuantitiesWithSelectedTableHeader } from '../../utils/Enums/TableHeaders';
import { CustomTable } from '../../components/CustomTable';
import { InventoryInner } from './InventoryInner';
import { usePagination } from '../../utils/pagination-hooks';
import { NothingSelectedComponent } from '../../components/NothingSelectedComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faWineBottle } from '@fortawesome/free-solid-svg-icons';
import { CustomButton } from '../../components/CustomButton';
import { useDebounce } from '../../utils/custom-hooks';
import {
    allocateManualCount,
    groupManualQuantities,
    manualAmountMl,
    sumManualAmountMl,
} from '../../utils/manual-handout-allocation';

const fetchAllQuantitiesForRefresh = useGetAllQuantities;
const INVENTORY_SEARCH_MIN_LENGTH = 3;
const INVENTORY_SEARCH_DEBOUNCE_MS = 500;

const getHandoutAmountMl = (quantity: ItemQuantity) => {
    return manualAmountMl(quantity, quantity.count);
};

const formatHandoutAmountMl = (quantity: ItemQuantity, mlLabel: string) => {
    const amountMl = getHandoutAmountMl(quantity);
    return amountMl != null ? `${amountMl} ${mlLabel}` : '—';
};

const PassportWithContainerMarker = ({ passport, hasContainer }: { passport: string; hasContainer?: boolean }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'manualHandout' });
    return (
    <span className='d-inline-flex align-items-center gap-1'>
        <span>{passport}</span>
        {hasContainer && <FontAwesomeIcon icon={faWineBottle} className='fa-fw' color='#0d6efd' title={t('withBottle')} />}
    </span>
    );
};

export const AddMultiHandout = ({
    itemRelatedId,
    setShowModal,
    refetchEntitiesHook,
    setCompleteButtonDisabled,
}: AddHandoutProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'table' });
    const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tManual } = useTranslation('translation', { keyPrefix: 'manualHandout' });
    const { handleSubmit, formState, control, reset } = useForm<ManualHandout>({
        mode: 'all',
    });
    const [getQuantities, fetchNewQuantities] = useAsyncResource(
        useGetAllQuantities,
        { filter: { id: itemRelatedId } } as TableRequestParams,
        true
    );

    const { items: cachedItemQuantities } = getQuantities();

    const [getDistributors] = useAsyncResource(useGetUsers, undefined, 'DISTRIBUTOR,INSTALLER');
    const distributors = getDistributors();
    const [distributor, setDistributor] = useState<User | null>();
    const [selectedDistributorId, setSelectedDistributorId] = useState<number>();
    const { loggedUser } = useContext(AuthContext);
    const operatorId = loggedUser?.id;
    const [invalidSelectedCounts, setInvalidSelectedCounts] = useState<Record<number, boolean>>({});
    const draftRef = useRef<{
        operationId: string;
        forUserId: number;
        warehouseId: number;
        operatorId: number;
        revision: number;
    }>();
    const reservationRef = useRef<Record<number, ManualReservation>>({});
    const [reservations, setReservations] = useState<Record<number, ManualReservation>>({});
    const [selectionRevision, setSelectionRevision] = useState(0);
    const [reservedRevision, setReservedRevision] = useState(-1);
    const [isReserving, setIsReserving] = useState(false);
    const [reservationError, setReservationError] = useState(false);
    const [reservationNow, setReservationNow] = useState(Date.now());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRefreshingQuantities, setIsRefreshingQuantities] = useState(false);
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const [targetCounts, setTargetCounts] = useState<Record<number, string>>({});
    const [differencesAcknowledged, setDifferencesAcknowledged] = useState(false);
    const [formInstanceId] = useState(createManualOperationId);
    const [freshItemQuantities, setFreshItemQuantities] = useState<ItemQuantity[]>(() =>
        cachedItemQuantities.map((item) => ({ ...item, selectedCount: undefined }))
    );
    const itemQuantities = freshItemQuantities;
    const generation = useRef(0);
    const refreshSequence = useRef(0);
    const blockingRefreshSequence = useRef(0);
    const mounted = useRef(true);
    const sending = useRef(false);
    const [confirmation, setConfirmation] = useState<ManualOperation>();
    const [pending, setPending] = useState<ManualOperation[]>(() =>
        pendingManualOperations(operatorId, Number(itemRelatedId))
    );
    const [getWarehouseList] = useAsyncResource(useGetWarehouses, {} as TableRequestParams);
    const warehouseName =
        getWarehouseList().items.find((warehouse) => warehouse.id === Number(itemRelatedId))?.warehouseName ??
        String(tManual('warehouseFallback', { id: itemRelatedId }));

    const selectedQuantities = selectedManualItems(itemQuantities);
    const selectedQuantitiesKey = selectedQuantities.map((item) => `${item.id}:${item.selectedCount}`).join('|');
    const updateLocalSelectedCount = (quantityId: number, selectedCount?: number) => {
        if (!selectedDistributorId || sending.current || confirmation || pending.length) return;
        if (draftRef.current) {
            draftRef.current.revision++;
            setSelectionRevision(draftRef.current.revision);
        }
        setIsReserving(true);
        setReservationError(false);
        setInvalidSelectedCounts((current) => ({ ...current, [quantityId]: false }));
        setFreshItemQuantities((current) =>
            current.map((item) => (item.id === quantityId ? { ...item, selectedCount } : item))
        );
    };

    const allocateTarget = (passportId: number, value: string) => {
        if (!selectedDistributorId || sending.current || confirmation || pending.length || isRefreshingQuantities)
            return;
        setTargetCounts((current) => ({ ...current, [passportId]: value }));
        const target = value === '' ? 0 : Number(value);
        if (!Number.isSafeInteger(target) || target < 0) return;
        if (draftRef.current) {
            draftRef.current.revision++;
            setSelectionRevision(draftRef.current.revision);
        }
        setIsReserving(true);
        setReservationError(false);
        setInvalidSelectedCounts((current) => {
            const next = { ...current };
            itemQuantities
                .filter((item) => item.passport.passportId === passportId)
                .forEach((item) => {
                    if (item.id != null) delete next[item.id];
                });
            return next;
        });
        setFreshItemQuantities((current) => {
            const allocated = allocateManualCount(
                current.filter((item) => item.passport.passportId === passportId),
                target
            );
            const byId = new Map(allocated.map((item) => [item.id, item]));
            return current.map((item) => (item.passport.passportId === passportId ? byId.get(item.id)! : item));
        });
    };

    const refreshAvailableQuantities = async (reservationDraft?: { operationId: string; revision: number }) => {
        const currentGeneration = generation.current;
        const sequence = ++refreshSequence.current;
        const background = !!reservationDraft;
        const isCurrent = () =>
            mounted.current &&
            generation.current === currentGeneration &&
            sequence === refreshSequence.current &&
            (!reservationDraft ||
                (draftRef.current?.operationId === reservationDraft.operationId &&
                    draftRef.current.revision === reservationDraft.revision));
        if (!background) {
            blockingRefreshSequence.current = sequence;
            setIsRefreshingQuantities(true);
        }
        try {
            const result = await fetchAllQuantitiesForRefresh(
                { filter: { id: itemRelatedId } } as TableRequestParams,
                true
            );
            if (!isCurrent()) return;
            // Availability is data; selection belongs exclusively to this form's current recipient.
            setFreshItemQuantities((current) => [
                ...result.items.map((item) => ({
                    ...item,
                    count:
                        item.count +
                        (reservationRef.current[item.id!] &&
                        new Date(reservationRef.current[item.id!].expiresAt).getTime() > Date.now()
                            ? reservationRef.current[item.id!].reservedCount
                            : 0),
                    selectedCount: current.find((previous) => previous.id === item.id)?.selectedCount,
                })),
                // Keep missing rows at zero so a target cannot silently disappear on refresh.
                ...current
                    .filter((item) => !result.items.some((fresh) => fresh.id === item.id))
                    .map((item) => ({ ...item, count: 0 })),
            ]);
        } catch {
            if (isCurrent()) {
                toast.error(tManual('refreshError'));
            }
        } finally {
            // Background refreshes may supersede the data, but must not take ownership of the UI lock.
            if (!background && mounted.current && generation.current === currentGeneration && sequence === blockingRefreshSequence.current) {
                setIsRefreshingQuantities(false);
            }
        }
    };

    const cancelCurrentDraft = () => {
        const draft = draftRef.current;
        draftRef.current = undefined;
        reservationRef.current = {};
        if (
            !draft ||
            pendingManualOperations(draft.operatorId, draft.warehouseId).some(
                (operation) => operation.request.operationId === draft.operationId
            )
        )
            return;
        cancelManualHandout({ ...draft, items: [] }).catch(() => {
            // If the connection is lost, the server's 15-minute expiry releases these reservations.
        });
    };

    useEffect(() => {
        const interval = setInterval(() => setReservationNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        mounted.current = true;
        generation.current++;
        sending.current = false;
        setIsSubmitting(false);
        setReservations({});
        setSelectionRevision(0);
        setReservedRevision(-1);
        setIsReserving(false);
        setReservationError(false);
        setFreshItemQuantities((current) => current.map((item) => ({ ...item, selectedCount: undefined })));
        setInvalidSelectedCounts({});
        setTargetCounts({});
        setDifferencesAcknowledged(false);
        setConfirmation(undefined);
        setDistributor(null);
        setSelectedDistributorId(undefined);
        setPending(pendingManualOperations(operatorId, Number(itemRelatedId)));
        reset();
        refreshAvailableQuantities();
        const syncPending = () => setPending(pendingManualOperations(operatorId, Number(itemRelatedId)));
        window.addEventListener('storage', syncPending);
        return () => {
            mounted.current = false;
            cancelCurrentDraft();
            window.removeEventListener('storage', syncPending);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operatorId, itemRelatedId]);

    const updateDistributor = (newValue: SingleValue<User>, onFieldChange: (value?: string) => void) => {
        if (sending.current) return;
        cancelCurrentDraft();
        generation.current++;
        if (newValue?.id && operatorId) {
            draftRef.current = {
                operationId: createManualOperationId(),
                forUserId: newValue.id,
                warehouseId: Number(itemRelatedId),
                operatorId,
                revision: 0,
            };
        }
        setReservations({});
        setSelectionRevision(0);
        setReservedRevision(-1);
        setIsReserving(false);
        setReservationError(false);
        setConfirmation(undefined);
        setFreshItemQuantities((current) => current.map((item) => ({ ...item, selectedCount: undefined })));
        setInvalidSelectedCounts({});
        setTargetCounts({});
        setDifferencesAcknowledged(false);
        setShowSelectedOnly(false);
        setSearchHandout('');
        setSelectedDistributorId(newValue?.id);
        onFieldChange(newValue?.username);
        setDistributor(newValue);
        refreshAvailableQuantities();
    };

    const hasConfirmedReservations =
        reservedRevision === selectionRevision &&
        selectedQuantities.length > 0 &&
        selectedQuantities.every((item) => {
            const reservation = reservations[item.id!];
            return (
                reservation?.reservedCount === item.selectedCount &&
                reservation.forUserId === selectedDistributorId &&
                new Date(reservation.expiresAt).getTime() > reservationNow
            );
        });
    const retryReservations = () => {
        if (!draftRef.current || sending.current || pending.length || confirmation) return;
        draftRef.current.revision++;
        setSelectionRevision(draftRef.current.revision);
        setIsReserving(true);
        setReservationError(false);
    };

    useEffect(() => {
        const draft = draftRef.current;
        if (!draft || pending.length || confirmation || sending.current) return;
        if (Object.values(invalidSelectedCounts).some(Boolean)) {
            setIsReserving(false);
            return;
        }
        const revision = draft.revision;
        const currentGeneration = generation.current;
        const timeout = setTimeout(async () => {
            setIsReserving(true);
            try {
                const result = await reserveManualHandout({
                    operationId: draft.operationId,
                    forUserId: draft.forUserId,
                    warehouseId: draft.warehouseId,
                    revision,
                    items: selectedQuantities.map((item) => ({
                        id: item.id!,
                        passportId: item.passport.passportId!,
                        warehouseId: draft.warehouseId,
                        batchCode: item.batchCode,
                        count: item.selectedCount!,
                    })),
                });
                if (
                    !mounted.current ||
                    currentGeneration !== generation.current ||
                    draftRef.current?.operationId !== draft.operationId ||
                    draftRef.current.revision !== revision ||
                    result.revision !== revision
                )
                    return;
                reservationRef.current = Object.fromEntries(
                    result.reservations.map((reservation) => [reservation.quantityId, reservation])
                );
                setReservations(reservationRef.current);
                setReservedRevision(revision);
                setReservationError(false);
                // Keep inputs editable; discard availability if the selection changes during this request.
                await refreshAvailableQuantities({ operationId: draft.operationId, revision });
            } catch {
                if (
                    mounted.current &&
                    currentGeneration === generation.current &&
                    draftRef.current?.revision === revision
                ) {
                    setReservationError(true);
                    setReservedRevision(-1);
                }
            } finally {
                if (
                    mounted.current &&
                    currentGeneration === generation.current &&
                    draftRef.current?.revision === revision
                )
                    setIsReserving(false);
            }
        }, 700);
        return () => clearTimeout(timeout);
        // Each selection edit increments revision synchronously; old responses cannot acknowledge a newer selection.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionRevision, selectedDistributorId, pending.length, confirmation, invalidSelectedCounts]);

    const useSubmitHandlerAdd = () => {
        if (
            sending.current ||
            confirmation ||
            isReserving ||
            !hasConfirmedReservations ||
            !draftRef.current ||
            isRefreshingQuantities ||
            pending.length ||
            !operatorId ||
            !distributor?.id ||
            !selectedQuantities.length ||
            hasInvalidTarget ||
            Object.values(invalidSelectedCounts).some(Boolean) ||
            selectedQuantities.some((item) => item.selectedCount! > item.count)
        )
            return;
        setDifferencesAcknowledged(false);
        setConfirmation({
            ...newManualOperation(
                operatorId,
                { id: distributor.id, username: distributor.username },
                Number(itemRelatedId),
                warehouseName,
                selectedQuantities,
                draftRef.current.operationId,
                reservations
            ),
            targetDifferences,
        });
    };

    const submitConfirmed = async () => {
        if (!confirmation || sending.current) return;
        if (confirmation.targetDifferences?.length && !differencesAcknowledged) return;
        const operation = confirmation;
        if (
            !pending.some((saved) => saved.request.operationId === operation.request.operationId) &&
            !hasConfirmedReservations
        ) {
            setConfirmation(undefined);
            setReservationError(true);
            return;
        }
        const currentGeneration = generation.current;
        // Persist the exact request before sending, so a lost response or closed tab can be retried safely.
        try {
            savePendingManualOperation(operation);
        } catch {
            toast.error(tManual('storageError'));
            return;
        }
        sending.current = true;
        setIsSubmitting(true);
        setPending(pendingManualOperations(operatorId, Number(itemRelatedId)));
        try {
            await manualHandoutBatch(operation.request);
            removePendingManualOperation(operation);
        } catch (error: any) {
            if (error?.manualHandoutRejected === operation.request.operationId) {
                removePendingManualOperation(operation);
                if (mounted.current && generation.current === currentGeneration) {
                    setConfirmation(undefined);
                    toast.error(tManual('rejected'));
                    setReservedRevision(-1);
                    setReservationError(true);
                    refreshAvailableQuantities();
                }
                return;
            }
            if (mounted.current && generation.current === currentGeneration)
                toast.error(tManual('unconfirmed'));
            return;
        } finally {
            if (generation.current === currentGeneration) sending.current = false;
            if (mounted.current && generation.current === currentGeneration) {
                setIsSubmitting(false);
                setPending(pendingManualOperations(operatorId, Number(itemRelatedId)));
            }
        }
        if (!mounted.current || generation.current !== currentGeneration) return;
        draftRef.current = undefined;
        reservationRef.current = {};
        setReservations({});
        setConfirmation(undefined);
        setFreshItemQuantities((current) => current.map((item) => ({ ...item, selectedCount: undefined })));
        refetchEntitiesHook();
        resourceCache(getQuantityCount).clear();
        resourceCache(useGetUserInventory).clear();
        resourceCache(useGetPassportTypesForWarehouse).clear();
        resourceCache(useGetAllQuantities).clear();
        resourceCache(useGetAllCompletedHandouts).clear();
        resourceCache(fetchTasksAll).clear();
        resourceCache(useGetAllTaskSkeletons).clear();
        resourceCache(useGetUserInventory).clear();
        resourceCache(fetchTasksYours).clear();
        resourceCache(fetchMapBranches).clear();
        resourceCache(useGetAllShortOtherMaterialPassports).clear();
        resourceCache(checkForItemsInInventory).clear();
        resourceCache(useGetReferencesForDistributor).clear();
        resourceCache(useGetReferences).clear();
        resourceCache(updateTaskNeededItems).clear();
        setShowModal(false);
        toast.success(<CustomToastContent content={[t('handoutSuccess')]} />, toastOptionsTopRight);
    };

    const selectedCountInput = (field: ItemQuantity, selectedCount: number | undefined, count: number) => (
        <div key={`quantity-${field.id}`} className='manual-handout-quantity-input'>
            <input
                aria-label={tManual('quantityLabel', { name: field.passport.passport })}
                type='number'
                min={0}
                max={count}
                step={1}
                value={selectedCount ?? ''}
                disabled={
                    !selectedDistributorId ||
                    isSubmitting ||
                    !!confirmation ||
                    pending.length > 0 ||
                    (count <= 0 && !selectedCount)
                }
                aria-invalid={(!!field.id && !!invalidSelectedCounts[field.id]) || (selectedCount ?? 0) > count}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => {
                    if (!field.id) return;
                    const value = event.target.value;
                    const number = Number(value);
                    if (value === '' || (Number.isInteger(number) && number >= 0 && number <= count)) {
                        updateLocalSelectedCount(field.id, value === '' ? undefined : number);
                    } else setInvalidSelectedCounts((current) => ({ ...current, [field.id as number]: true }));
                }}
            />
            <div className='manual-handout-quantity-status'>
                <small
                    className='text-success'
                    style={{
                        visibility:
                            field.id &&
                            selectedCount &&
                            reservations[field.id]?.reservedCount === selectedCount &&
                            reservedRevision === selectionRevision &&
                            new Date(reservations[field.id].expiresAt).getTime() > reservationNow
                                ? 'visible'
                                : 'hidden',
                    }}
                >
                    {tManual('reserved', { count: selectedCount ?? 0 })}
                </small>
                <small
                    className='text-danger'
                    style={{
                        visibility:
                            (!!field.id && !!invalidSelectedCounts[field.id]) || (selectedCount ?? 0) > count
                                ? 'visible'
                                : 'hidden',
                    }}
                >
                    {t('selectedCountRangeWithZero', { count })}
                </small>
            </div>
        </div>
    );

    const [searchHandout, setSearchHandout] = useState<string>('');
    const debouncedSearchHandout = useDebounce(searchHandout, INVENTORY_SEARCH_DEBOUNCE_MS);
    const trimmedInventorySearch = debouncedSearchHandout.trim();
    const effectiveInventorySearch =
        trimmedInventorySearch.length >= INVENTORY_SEARCH_MIN_LENGTH ? trimmedInventorySearch : '';
    const [filter, setFilter] = useState<Filter>({ tags: [], search: '' });
    const normalizedSearchHandout = searchHandout.toLowerCase();
    const filteredHangouts = itemQuantities.filter((hangOut) => {
        const passport = hangOut.passport?.passport?.toLowerCase() ?? '';
        const batchCode = hangOut.batchCode?.toLowerCase() ?? '';

        return passport.includes(normalizedSearchHandout) || batchCode.includes(normalizedSearchHandout);
    });
    const visibleHandouts = filteredHangouts.filter(({ count, selectedCount }) => count > 0 || !!selectedCount);
    const displayedHandouts = showSelectedOnly
        ? visibleHandouts.filter((handout) => handout.selectedCount && handout.selectedCount > 0)
        : visibleHandouts;
    const selectedHandoutsTotalCount = selectedQuantities.reduce(
        (totalCount, handout) => totalCount + (handout.selectedCount ?? 0),
        0
    );
    const hasInvalidSelectedCount =
        Object.values(invalidSelectedCounts).some(Boolean) ||
        selectedQuantities.some((item) => item.selectedCount! > item.count);
    const groups = groupManualQuantities(itemQuantities);
    const hasInvalidTarget = Object.values(targetCounts).some(
        (value) => value !== '' && (!Number.isSafeInteger(Number(value)) || Number(value) < 0)
    );
    const targetDifferences = groups.flatMap(({ passportId, rows }) => {
        const value = targetCounts[passportId];
        if (value == null || value === '') return [];
        const target = Number(value);
        const selected = rows.reduce((sum, item) => sum + (item.selectedCount ?? 0), 0);
        return target === selected
            ? []
            : [{ passportId, name: rows[0].passport.passport, target, selected }];
    });
    const displayedIds = new Set(displayedHandouts.map((item) => item.id));
    const displayedGroups = groups.filter(
        ({ passportId, rows }) =>
            rows.some((item) => displayedIds.has(item.id)) ||
            (!showSelectedOnly &&
                Number(targetCounts[passportId]) > 0 &&
                rows.some((item) => item.passport.passport.toLowerCase().includes(normalizedSearchHandout)))
    );

    useEffect(() => {
        setFilter((current) => ({ ...current, search: effectiveInventorySearch }));
    }, [effectiveInventorySearch]);

    useEffect(() => {
        setCompleteButtonDisabled?.(
            isSubmitting ||
                isReserving ||
                !hasConfirmedReservations ||
                isRefreshingQuantities ||
                hasInvalidSelectedCount ||
                hasInvalidTarget ||
                !selectedDistributorId ||
                !selectedQuantities.length ||
                !!confirmation ||
                pending.length > 0
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isSubmitting,
        isReserving,
        hasConfirmedReservations,
        isRefreshingQuantities,
        hasInvalidSelectedCount,
        hasInvalidTarget,
        selectedDistributorId,
        selectedQuantitiesKey,
        confirmation,
        pending.length,
        setCompleteButtonDisabled,
    ]);

    let reservationMessageIndex = 3;
    if (isReserving) {
        reservationMessageIndex = 0;
    } else if (reservationError) {
        reservationMessageIndex = 1;
    } else if (hasConfirmedReservations) {
        reservationMessageIndex = 2;
    }

    return (
        <>
            <Alert className='manual-handout-reservation-alert' variant={reservationError ? 'warning' : 'info'}>
                <div className='manual-handout-reservation-message'>
                    {[
                        'reserving',
                        'reservationFailed',
                        'reservationConfirmed',
                        'reservationHint',
                    ].map((message, index) => (
                        <span
                            key={message}
                            aria-hidden={index !== reservationMessageIndex}
                            style={{
                                visibility:
                                    index === reservationMessageIndex
                                        ? 'visible'
                                        : 'hidden',
                            }}
                        >
                            {tManual(message)}
                        </span>
                    ))}
                </div>
                <Button
                    variant='link'
                    hidden={!(selectedQuantities.length > 0 && !hasConfirmedReservations && !isReserving)}
                    disabled={isSubmitting || pending.length > 0 || !!confirmation}
                    onClick={retryReservations}
                >
                    {tManual('retry')}
                </Button>
            </Alert>
            {pending.length > 0 && (
                <Alert variant='warning'>
                    {tManual('pending')}
                    {pending.map((operation) => (
                        <Button
                            key={operation.request.operationId}
                            variant='link'
                            disabled={isSubmitting}
                            onClick={() => {
                                setDifferencesAcknowledged(false);
                                setConfirmation(operation);
                            }}
                        >
                            {operation.recipientName} — {operation.warehouseName} — {tManual('positions', { count: operation.request.items.length })}
                        </Button>
                    ))}
                </Alert>
            )}
            <Modal
                scrollable
                dialogClassName='manual-handout-confirmation-dialog'
                show={!!confirmation}
                onHide={() => !sending.current && setConfirmation(undefined)}
                backdrop='static'
            >
                <Modal.Header closeButton={!isSubmitting}>
                    <Modal.Title>{tManual('confirmationTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {tManual('recipient')} <strong>{confirmation?.recipientName}</strong> (№{confirmation?.request.forUserId})
                    </p>
                    <p>
                        {tManual('warehouse')} <strong>{confirmation?.warehouseName}</strong> (№{confirmation?.request.warehouseId})
                    </p>
                    <div className='table-responsive'>
                        <table className='table table-sm manual-handout-confirmation-table' aria-label={tManual('resources')}>
                            <thead>
                                <tr>
                                    <th scope='col'>{t('no')}</th>
                                    <th scope='col'>{t('passportName')}</th>
                                    <th scope='col'>{t('batchCode')}</th>
                                    <th scope='col' className='text-end'>{t('count')} ({tCommon('pcs')})</th>
                                    <th scope='col' className='text-end'>{t('handoutAvailableAmount')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {confirmation?.lines.map((line, index) => (
                                    <tr key={line.id}>
                                        <td>{index + 1}</td>
                                        <td>{line.name}</td>
                                        <td>{line.batchCode || '—'}</td>
                                        <td className='text-end fw-bold'>{line.count}</td>
                                        <td className='text-end fw-bold'>{line.amountMl ?? '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p>{tManual('issueTogether')}</p>
                    {!!confirmation?.targetDifferences?.length && (
                        <Alert variant='warning'>
                            <strong>{tManual('differences')}</strong>
                            <ul>
                                {confirmation.targetDifferences.map((difference) => (
                                    <li key={difference.passportId ?? difference.name}>
                                        {tManual(difference.selected < difference.target ? 'differenceRemaining' : 'differenceExcess', {
                                            name: difference.name, target: difference.target, selected: difference.selected,
                                            difference: Math.abs(difference.target - difference.selected),
                                        })}
                                        {difference.targetMl != null && (
                                            <span>
                                                {' '}
                                                {tManual('volumeDifference', { target: difference.targetMl, selected: difference.selectedMl ?? '—' })}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <Form.Check
                                id={`handout-differences-${formInstanceId}`}
                                label={tManual('acknowledge')}
                                checked={differencesAcknowledged}
                                disabled={isSubmitting}
                                onChange={(event) => setDifferencesAcknowledged(event.target.checked)}
                            />
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' disabled={isSubmitting} onClick={() => setConfirmation(undefined)}>
                        {tCommon('previous')}
                    </Button>
                    <Button
                        disabled={
                            isSubmitting || (!!confirmation?.targetDifferences?.length && !differencesAcknowledged)
                        }
                        onClick={submitConfirmed}
                    >
                        {tManual(isSubmitting ? 'submitting' : 'confirm')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Form
                id='form'
                aria-label={tManual('form')}
                className='manual-handout-form d-flex flex-column align-items-center w-100'
                style={{ padding: '8px 0' }}
                noValidate
                onSubmit={handleSubmit(useSubmitHandlerAdd)}
            >
                <Row className='d-flex align-items-center justify-content-between w-100 mb-1'>
                    <Col xs={12} md={6} className='px-1'>
                        <Form.Group
                            className=' d-flex flex-column mb-2 w-100 align-items-start ms-0 ms-mb-3'
                            controlId='forUser'
                        >
                            <Form.Label>{t('forUser')}</Form.Label>
                            <Controller
                                control={control}
                                name='forUser'
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <>
                                        <Select
                                            className='w-100'
                                            value={distributor ?? null}
                                            isDisabled={isSubmitting || !!confirmation}
                                            onChange={(newValue) => updateDistributor(newValue, field.onChange)}
                                            options={distributors.items}
                                            getOptionLabel={(distributor) => distributor.username}
                                            getOptionValue={(distributor) => distributor.username}
                                            styles={distributorSelectStyles}
                                        />
                                        {formState.errors.forUser && (
                                            <p className={`input-error`}>{'*' + t('choose')}</p>
                                        )}
                                    </>
                                )}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6} className='px-1'>
                        <Form.Group className='mb-2'>
                            <Form.Label>{t('filterProducts')}</Form.Label>
                            <Form.Control
                                placeholder={t('search')}
                                name={'Search'}
                                onChange={(e) => {
                                    setSearchHandout(e.currentTarget.value);
                                }}
                                className='manual-handout-product-filter mt-0 mb-0'
                                value={searchHandout}
                            ></Form.Control>
                        </Form.Group>
                    </Col>
                </Row>
                <Row className='manual-handout-panels w-100'>
                    <Col xs={12} lg={5} xl={4}>
                        <div className='d-flex flex-column align-items-center px-1 py-0 w-100 wrapper-handout'>
                            {selectedDistributorId ? (
                                <Suspense fallback={<Spinner animation='border' />}>
                                    <UserInventoryTable userId={selectedDistributorId} filter={filter} />
                                </Suspense>
                            ) : (
                                <NothingSelectedComponent
                                    nothingSelectedText={t('distributor')}
                                ></NothingSelectedComponent>
                            )}
                        </div>
                    </Col>
                    <Col xs={12} lg={7} xl={8}>
                        {itemQuantities.length > 0 ? (
                            <div className='d-flex flex-column align-items-center px-1 py-0 w-100 wrapper-handout handout-quantities-table'>
                                <div className='w-100 d-flex align-items-center justify-content-between my-1 position-relative'>
                                    <div>
                                        <Form.Label htmlFor='previewSelectedHandouts' className='mx-2 mb-0'>
                                            {t('preview')}
                                        </Form.Label>
                                        <Form.Check.Input
                                            id='previewSelectedHandouts'
                                            type='checkbox'
                                            className='my-0 mx-1'
                                            checked={showSelectedOnly}
                                            disabled={
                                                isRefreshingQuantities || isReserving || isSubmitting || !!confirmation
                                            }
                                            onChange={(event) => setShowSelectedOnly(event.currentTarget.checked)}
                                        />
                                    </div>
                                    <CustomButton
                                        className='text-secondary text-decoration-none border-0 bg-transparent px-3 py-2'
                                        variant='link'
                                        icon={faSync}
                                        iconClass={`fa-fw ${isRefreshingQuantities ? 'fa-spin' : ''}`}
                                        iconSize='lg'
                                        tooltipTitle={t('refreshQuantities')}
                                        disabled={
                                            isRefreshingQuantities || isReserving || isSubmitting || !!confirmation
                                        }
                                        onClick={() => refreshAvailableQuantities()}
                                    />
                                </div>
                                <section className='manual-handout-scroll w-100' aria-label={tManual('resources')}>
                                {displayedGroups.map(({ passportId, rows }) => {
                                    const first = rows[0];
                                    const name = first.passport.passport;
                                    const liquid = first.trackingType === 'LIQUID_ML';
                                    const count = rows.reduce((sum, item) => sum + item.count, 0);
                                    const selected = rows.reduce((sum, item) => sum + (item.selectedCount ?? 0), 0);
                                    const value = targetCounts[passportId] ?? '';
                                    const target = value === '' ? undefined : Number(value);
                                    const validTarget = target != null && Number.isSafeInteger(target) && target >= 0;
                                    const selectedMl = sumManualAmountMl(rows, true);
                                    const targetMl =
                                        validTarget && first.oilCapacity != null
                                            ? target! * first.oilCapacity
                                            : undefined;
                                    const remaining = validTarget ? target! - selected : undefined;
                                    const mlText = (amount?: number) => {
                                        if (!liquid) return '';

                                        const formattedAmount = amount == null ? '—' : amount.toLocaleString('bg-BG');

                                        return ` / ${formattedAmount} ${tCommon('ml')}`;
                                    };
                                    const disabled =
                                        !selectedDistributorId ||
                                        isSubmitting ||
                                        isRefreshingQuantities ||
                                        !!confirmation ||
                                        pending.length > 0;
                                    const groupRows = rows.filter((item) =>
                                        showSelectedOnly ? !!item.selectedCount : item.count > 0 || !!item.selectedCount
                                    );
                                    return (
                                        <section
                                            key={passportId}
                                            className='manual-handout-product border rounded w-100'
                                            aria-label={tManual('product', { name })}
                                        >
                                            <div className='manual-handout-product-heading'>
                                                <strong>{name}</strong>
                                                <span>{tManual('stock', { count })}{mlText(sumManualAmountMl(rows))}</span>
                                            </div>
                                            <div className='manual-handout-product-controls d-flex flex-wrap align-items-center gap-2'>
                                                <div className='d-flex align-items-center gap-2'>
                                                <label htmlFor={`handout-target-${formInstanceId}-${passportId}`}>
                                                    {tManual('target')}
                                                </label>
                                                <input
                                                    id={`handout-target-${formInstanceId}-${passportId}`}
                                                    aria-label={tManual('targetLabel', { name })}
                                                    className='form-control manual-handout-target-input'
                                                    type='number'
                                                    min={0}
                                                    step={1}
                                                    value={value}
                                                    disabled={disabled}
                                                    aria-invalid={value !== '' && !validTarget}
                                                    onChange={(event) => allocateTarget(passportId, event.target.value)}
                                                />
                                                {liquid && <span>{mlText(targetMl)}</span>}
                                                </div>
                                            <div aria-live='polite'>
                                                {tManual('selected', { count: selected })}{mlText(selectedMl)}
                                                {remaining != null && remaining !== 0 && (
                                                    <span className='text-warning'>
                                                        {' · '}
                                                        {tManual(remaining < 0 ? 'excess' : 'remaining')}: {Math.abs(remaining)}{' '}
                                                        {tCommon('pcs')}
                                                    </span>
                                                )}
                                             </div>
                                                <Button
                                                    className='ms-auto'
                                                    variant='outline-secondary'
                                                    size='sm'
                                                    disabled={disabled || !validTarget}
                                                    onClick={() => allocateTarget(passportId, value)}
                                                >
                                                    {tManual('allocate')}
                                                </Button>
                                            </div>
                                            {value !== '' && !validTarget && (
                                                <div className='text-danger'>{tManual('invalidTarget')}</div>
                                            )}
                                            <details>
                                                <summary className='manual-handout-product-summary'>
                                                    {tManual('batches', { count: groupRows.length })}
                                                </summary>
                                                <small>
                                                    {tManual('batchHint')}
                                                </small>
                                                <CustomTable
                                                    columns={{
                                                        headings: shortHandoutQuantitiesWithSelectedTableHeader,
                                                        items: groupRows.map((value) => [
                                                            <PassportWithContainerMarker
                                                                key={`passport-${value.id}`}
                                                                passport={value.passport.passport}
                                                                hasContainer={value.hasContainer}
                                                            />,
                                                            <span key={`batch-${value.id}`}>
                                                                {value.batchCode}
                                                                <br />
                                                                <small>
                                                                    {value.deliveryDate &&
                                                                    Number.isFinite(
                                                                        new Date(value.deliveryDate).getTime()
                                                                    )
                                                                        ? new Date(
                                                                              value.deliveryDate
                                                                          ).toLocaleDateString('bg-BG')
                                                                        : tManual('noDeliveryDate')}
                                                                </small>
                                                            </span>,
                                                            t(value.state ?? ''),
                                                            <span key={`count-${value.id}`} className='handout-count-cell'>
                                                                {`${value.count} ${tCommon('pcs')}`}
                                                            </span>,
                                                            formatHandoutAmountMl(value, tCommon('ml')),
                                                            selectedCountInput(value, value.selectedCount, value.count),
                                                        ]),
                                                    }}
                                                    viewComponent={(id) => {
                                                        const fields = itemQuantities.find((item) => item.id?.toString() === id);

                                                        if (!fields?.id || fields.count <= 0) return;

                                                        const selectedCount = fields.selectedCount
                                                            ? Math.min(fields.selectedCount + 1, fields.count)
                                                            : 1;
                                                        updateLocalSelectedCount(fields.id, selectedCount);
                                                    }}
                                                    tableData={groupRows}
                                                    // actionIcon={faMinusCircle}
                                                    // actionHook={(fields) => {
                                                    //     fields.selectedCount && fields.selectedCount > 0
                                                    //         ? fields.selectedCount--
                                                    //         : (fields.selectedCount = 0);
                                                    //     fetchNewQuantities({ filter: { id: itemRelatedId } } as TableRequestParams, true);
                                                    // }}
                                                    // hideActionModal={true}
                                                    {...{ fetchNewQuantities }}
                                                />
                                            </details>
                                        </section>
                                    );
                                })}

                                <Row className='w-100 mx-0'>
                                    <Col className='col-12 px-0'>
                                        <p className='d-flex justify-content-between'>
                                            <span>
                                                <strong className='font-bold'>{t('numberOfRows')}</strong>{' '}
                                                {displayedHandouts.length}
                                            </span>
                                            <span>
                                                <strong className='font-bold'>{t('totalHandoutCount')}</strong>{' '}
                                                {selectedHandoutsTotalCount} {tCommon('pcs')}
                                            </span>
                                        </p>
                                    </Col>
                                </Row>
                                </section>
                            </div>
                        ) : (
                            <div className='h-100 d-flex align-items-center w-100'>
                                <NoDataComponent noDataText={t('quantities')}></NoDataComponent>
                            </div>
                        )}
                    </Col>
                </Row>
            </Form>
            <Row></Row>
        </>
    );
};

const UserInventoryTable = ({ userId, filter }: { userId: number; filter: Filter }) => {
    const inventoryPagination = usePagination(); // 100

    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [forSale, setForSale] = useState(false);

    const [inventoryFilter, setInventoryFilter] = useState({
        ...filter,
        id: userId.toString(),
        ...(forSale ? { forSale } : {}),
    });

    const [getInventory, fetchNewInventory] = useAsyncResource(
        useGetUserInventory,
        inventoryPagination,
        inventoryFilter
    );

    // const userInventory = getInventory();
    const refetchInventoryHook = async () => {
        resourceCache(useGetWarehouses).clear();
        resourceCache(useGetUserInventory).clear();
        resourceCache(useGetAllQuantities).clear();

        await fetchNewInventory(inventoryPagination, inventoryFilter);
    };

    const changeOnlyForSell = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newForSale = event.target.checked;
        setForSale(newForSale);
        setInventoryFilter({ ...filter, id: userId.toString(), ...(newForSale ? { forSale: newForSale } : {}) });
    };

    useEffect(() => {
        const newFilter = { ...filter, id: userId.toString(), ...(forSale ? { forSale } : {}) };
        setInventoryFilter(newFilter);
        fetchNewInventory(inventoryPagination, newFilter);
    }, [userId, forSale, filter]);

    return (
        <>
            <div className='my-3'>
                <Form.Label htmlFor='isForSale' className='mx-2 mb-0'>
                    {t('showForSale')}
                </Form.Label>
                <Form.Check.Input
                    id='isForSale'
                    type='checkbox'
                    className='my-0 mx-1'
                    onChange={changeOnlyForSell}
                    defaultChecked={false}
                />
            </div>
            <section className='manual-handout-scroll w-100' aria-label={t('manualHandout.inventory', { keyPrefix: '' })}>
            <InventoryInner
                getEntities={getInventory}
                pagination={inventoryPagination}
                refetchEntitiesHook={refetchInventoryHook}
                showSelect={false}
                showPagination={false}
                showRows={true}
            />
            </section>
        </>
    );
};
